/**
 * My Profile API — backend calls for the logged-in user's profile page.
 *
 * Uses the same user-manage endpoints as the admin Users page, but with
 * self-targeted actions:
 *   - "update"     → edit own profile (email, display name, groups)
 *   - "reset-self" → change own password (requires current password)
 *
 * Cloud:  VITE_USER_V2_ENDPOINT (/user)        → apiUserManageCloud
 * Legacy: VITE_USER_ENDPOINT    (/user_manager) → apiUserManageLegacy
 */

import { apiFetch, apiUserManageCloud, apiUserManageLegacy } from '../../../../services/api.js';
import { getCampusConfig } from '../../../../services/session.js';
// The self-edit goes through the shared user-manage mapper; never hand-roll it.
import { editUser } from '../../../users/api/usersApi.js';
import { fetchUserProfile } from '../../../users/user-profile/api/userProfileApi.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns true if the current campus is cloud-enabled */
function isCloud() {
  return !!getCampusConfig()?.is_cloud;
}

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch all user groups for a campus (for group name lookup + group editing).
 *
 * Procedure: sp_dev_getgroupsbycampusid
 */
export function fetchUserGroups(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getgroupsbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Edit the logged-in user's own profile (email, and display name / groups where
 * the user may edit them).
 *
 * Routed through usersApi.editUser, the same call the Users page and the user
 * profile page make. It used to hand-roll both payloads, sending neither phone,
 * title nor the SIP fields: both Lambdas default a missing key to blank ('' / 0)
 * and pass it positionally to sp_editUser, so a self-edit could wipe the user's own
 * phone, title and SIP extension. The legacy branch also sent no OldPayload, so its
 * audit entry had no "before".
 *
 * So the save first loads the user's current record (getuserprofile, as
 * UserProfilePage does) and carries every field through unchanged apart from the
 * ones edited here. If that load fails, NOTHING is sent: refusing the save beats
 * risking a blank overwrite.
 *
 * Two behaviours are deliberately kept from the old self-edit:
 *   - Migrated is the user's own flag, not editUser's admin default of true.
 *   - is_grandstream is sent only when the user already has an extension. With it
 *     set and no extension, both Lambdas PROVISION a new SIP account; a self-edit of
 *     an email address must not do that. With an extension, it keeps the PBX name
 *     in sync on a rename, as an admin edit does.
 *
 * @param {object} data — { email, display_name?, group_ids? }
 * @param {object} session — { campusId, userId, clientId, username, roleId, cognitoId,
 *                              displayName, email: currentEmail, groups, displayEdit, groupEdit, migrated }
 * @returns {Promise<[boolean, string?]>}
 */
export async function editSelfProfile(data, session) {
  let current = null;
  try {
    const rows = await fetchUserProfile(session.campusId, session.userId);
    current = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
  } catch {
    current = null;
  }
  if (!current) {
    return [false, 'Could not load your current profile, so nothing was saved. Please try again.'];
  }

  const hasSip = Boolean(current.sip_extension);
  const grandstream = Boolean(getCampusConfig()?.is_grandstream) && hasSip;
  const base = {
    user_id: session.userId,
    username: session.username,
    user_role_id: current.user_role_id ?? session.roleId,
    cognito_id: current.cognito_id ?? session.cognitoId,
    phone: current.phone,
    title: current.title,
    sip_extension: current.sip_extension,
    wave_password: current.wave_password,
    is_grandstream: grandstream,
  };
  const oldData = {
    ...base,
    display_name: session.displayName,
    email: session.email,
    group_ids: session.groups || [],
  };
  const newData = {
    ...base,
    display_name: session.displayEdit ? data.display_name : session.displayName,
    email: (data.email || '').toLowerCase(),
    group_ids: session.groupEdit ? (data.group_ids || []) : (session.groups || []),
  };
  return editUser(newData, oldData, {
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
    migrated: Boolean(session.migrated),
  });
}

/**
 * Reset the logged-in user's own password.
 *
 * Requires current password verification (action: "reset-self").
 *
 * Cloud:  user-manage-2  action: "reset-self"
 * Legacy: user-manage     Action: "reset-self"
 *
 * @param {object} data — { currentPassword, password }
 * @param {object} session — { campusId, userId, clientId, username, migrated }
 * @returns {Promise<[boolean, string?]>}
 */
export function resetSelfPassword(data, session) {
  if (isCloud()) {
    const payload = [{
      action: 'reset-self',
      campus_id: session.campusId,
      username: session.username,
      password: data.password,
      orig_pass: data.currentPassword,
      user_id: session.userId,
      modified_by: session.userId,
      origPayload: {},
    }];
    return apiUserManageCloud({
      payload,
      clientId: session.clientId,
      userId: session.userId,
    });
  }

  // Legacy
  const payload = [{
    Action: 'reset-self',
    CampusId: session.campusId,
    Username: session.username,
    UserPass: data.password,
    OrigPass: data.currentPassword,
    Migrated: session.migrated || false,
    ClientId: session.clientId,
  }];
  return apiUserManageLegacy({
    payload,
    userId: session.userId,
  });
}
